import { ConfigMap } from "../interfaces/configmap";
import { GroupBlockData } from "../interfaces/groupblock";
import { GroupBlockEntityType } from "../interfaces/entity";
import http from "../../common/utilities/HttpModule";
import { Annotation } from "../../annotator/interfaces/annotation";
import {
  CategorizationAnnotation,
  CategorizationAnnotationCreate,
} from "../interfaces/category";

const TEMP_ANNOTATION_PREFIX = "temp";

const transformAndFilterGroupBlocks = (
  groupBlocks: Array<GroupBlockData>,
  filterRequired: boolean,
): Array<GroupBlockEntityType> => {
  return [...groupBlocks]
    .sort((a, b) => a.index - b.index)
    .map((block) => ({
      ...block,
      groupBlockEntityTypes: [...(block.groupBlockEntityTypes ?? [])].sort(
        (a, b) => a.index - b.index,
      ),
    }))
    .flatMap((block) =>
      block.groupBlockEntityTypes.map((entity) => ({
        ...entity,
        multipleGroupBlocks: block.multipleGroupBlocks,
        groupBlockId: block.id,
      })),
    )
    .filter((entity) => !filterRequired || entity.required);
};

export const getFlatEntityListFromConfigMap = (
  configMap: ConfigMap | undefined,
  filterRequired = false,
): Array<GroupBlockEntityType> => {
  if (!configMap?.groupBlocks?.length) {
    return [];
  }

  return transformAndFilterGroupBlocks(configMap.groupBlocks, filterRequired);
};

export const getFlatEntityListFromGroupBlocks = (
  groupBlocks: Array<GroupBlockData> | undefined,
  filterRequired = false,
): Array<GroupBlockEntityType> => {
  if (!groupBlocks?.length) {
    return [];
  }

  return transformAndFilterGroupBlocks(groupBlocks, filterRequired);
};

const prepareEntityAnnotationsForDocument = (
  documentId: string,
  annotations: Array<Annotation> | null,
) => {
  const documentAnnotations =
    annotations?.filter((annotation) => annotation.documentId === documentId) ??
    [];

  return documentAnnotations
    .filter((annotation) => !annotation.tempAnnotation)
    .map((annotation) => ({
      id: annotation.id.includes(TEMP_ANNOTATION_PREFIX) ? null : annotation.id,
      entityTypeId: annotation.entity.id,
      page: annotation.page,
      pageTokenIndices: annotation.pageTokenIndices,
      value: annotation.values?.length ? annotation.values.join(" ") : null,
      modelScore: annotation.modelScore,
      index: annotation.index,
      documentId,
      isByUser: annotation.isByUser,
      isOutput: annotation.isOutput,
      entityAnnotationNormalization: annotation.entityAnnotationNormalization,
    }));
};

const prepareCategorizationAnnotationsForDocument = (
  documentId: string,
  categorizationAnnotations: Array<CategorizationAnnotation>,
): Array<CategorizationAnnotationCreate> => {
  return categorizationAnnotations
    .filter((annotation) => annotation.documentId === documentId)
    .map((annotation): CategorizationAnnotationCreate => ({
      ...annotation,
      index: annotation.index ?? 0,
      isByDefault: false,
      isByUser: annotation.isByUser ?? false,
      categoryValue: annotation.value,
      categoryVisibleValue: annotation.visibleValue,
    }));
};

export const postData = (
  documentIds: Array<string>,
  annotations: Array<Annotation> | null,
  categorizationAnnotations: Array<CategorizationAnnotation>,
) => {
  return Promise.all(
    documentIds.map((documentId) => {
      const batchPayload = {
        entityAnnotations: prepareEntityAnnotationsForDocument(
          documentId,
          annotations,
        ),
        categorizationAnnotations: prepareCategorizationAnnotationsForDocument(
          documentId,
          categorizationAnnotations,
        ),
      };

      return http.post(`api/v1/documents/${documentId}/batch`, batchPayload);
    }),
  );
};
